import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { SubHubGrid, ContentSection, FaqBlock, buildFaqLd, buildBreadcrumbLd, ResultCard, ReservedResultsArea } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, WhatsAppShareButton, useAnalyzeGate, useTicker, todayISO } from "@/components/age-calculator/premium";
import { AgeInsightCards } from "@/components/age-calculator/insight-cards";
import { ALL_TOOLS, CLUSTER_EMOJI } from "@/lib/utility-tools/registry";

const TITLE = "Age Calculator Online: Exact Age, Heartbeats & Planetary Age (2026)";
const H1 = "Age Calculator Online: Exact Age from Date of Birth";
const DESC = "Calculate your exact age from date of birth in years, months, days, total heartbeats, breaths, and age on Mars & Jupiter. Free live ticking age counter tool.";

const KEYWORDS =
  "age calculator, calculate age, DOB calculator, exact age calculator, age in days, planetary age calculator, heartbeats calculator, life path number finder";

const MASTER_LD = {
  type: "application/ld+json" as const,
  children: JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        "@id": "https://drhint.com/age-calculator#webapp",
        url: "https://drhint.com/age-calculator",
        name: "Exact Age & Life Statistics Calculator",
        applicationCategory: "UtilityApplication",
        operatingSystem: "All",
        description:
          "Calculates exact age in years, months, days, hours, live ticking seconds, lifetime heartbeats, breaths taken, and age on Mars, Venus, and Jupiter.",
      },
      {
        "@type": "FAQPage",
        "@id": "https://drhint.com/age-calculator#faq",
        mainEntity: [
          ["how many heartbeats have I had since I was born calculator", "Your total heartbeats are calculated by multiplying your total minutes lived by an average resting heart rate of 72 beats per minute (BPM)."],
          ["exact number of breaths taken in lifetime calculator by date of birth", "Lifetime breaths taken are calculated by multiplying your total lived minutes by an average respiratory rate of 16 breaths per minute."],
          ["how old am I on planet mars in earth years calculator", "Since Mars takes 687 Earth days to orbit the Sun, divide your current Earth age by 1.88 to find your exact age on Mars."],
          ["how many hours have I slept in my entire life age calculator", "Total time spent asleep is estimated by multiplying your total lived days by an average sleep duration of 8 hours per day."],
          ["calculate my age in total weeks days hours and minutes online", "Enter your Date of Birth in the inputs above. Our calculator computes the precise time difference considering leap years and calendar variations."],
          ["life path number zodiac sign and birthstone finder from DOB", "By selecting your birth date, the tool automatically calculates your Life Path number via digit reduction and determines your tropical zodiac sign and traditional birthstone."],
          ["how many days until I turn 30 landmark countdown calculator", "Our landmark countdown matrix automatically calculates the remaining days, hours, and minutes left before you reach major milestone birthdays like turning 30, 40, or 50."],
        ].map(([name, text]) => ({
          "@type": "Question",
          name,
          acceptedAnswer: { "@type": "Answer", text },
        })),
      },
    ],
  }),
};

const FAQS = [
  { q: "How does an age calculator by DOB work?", a: "It takes your date of birth and the target date (today by default), then subtracts calendar years, months and days while accounting for leap years so the total is exact — not an estimate." },
  { q: "How old am I today, precisely?", a: "Enter your date of birth above and keep 'Today' as the target date. You'll instantly see your age in years-months-days plus total weeks, days, hours and minutes lived." },
  { q: "Is this calculator accurate for legal or job applications?", a: "Yes. The logic uses the standard chronological method used by government forms, exam boards and HR systems — years first, then remaining months, then remaining days." },
  { q: "Can I calculate age between two custom dates?", a: "Yes — change the target date to any past or future date. For deeper date-difference outputs (total weeks, intervening months), use our Chronological & Date Difference Calculator." },
  { q: "Does it work for future birthdays?", a: "If your target date is before the date of birth, the tool tells you how many days until that birthday instead of returning a negative age." },
];

export const Route = createFileRoute("/age-calculator/")({
  head: () =>
    leafHead({
      path: "/age-calculator",
      title: TITLE,
      description: DESC,
      extraMeta: [{ name: "keywords", content: KEYWORDS }],
      scripts: [
        MASTER_LD,
        buildFaqLd(FAQS),
        buildBreadcrumbLd([
          { name: "Home", url: "https://drhint.com/" },
          { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        ]),
      ],
    }),
  component: AgeCalculatorHub,
});

function AgeCalculatorHub() {
  const today = todayISO();
  const [dob, setDob] = useState("");
  const [target, setTarget] = useState(today);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  useTicker(revealed); // second-by-second live updates

  const canCalculate = !!dob && !!target;

  const onCalculate = () => { if (!canCalculate) return; trigger(); };
  const onEditDob = (v: string) => { setDob(v); reset(); };
  const onEditTarget = (v: string) => { setTarget(v); reset(); };

  const result = useMemo(() => {
    if (!dob || !target) return null;
    const a = new Date(dob);
    const b = new Date(target);
    if (isNaN(a.getTime()) || isNaN(b.getTime())) return null;
    const forward = b >= a;
    const [from, to] = forward ? [a, b] : [b, a];
    let years = to.getFullYear() - from.getFullYear();
    let months = to.getMonth() - from.getMonth();
    let days = to.getDate() - from.getDate();
    if (days < 0) {
      months -= 1;
      days += new Date(to.getFullYear(), to.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }
    // Live ticking: recompute against real "now" when target is today so seconds tick.
    const bNow = target === today ? new Date() : b;
    const msDiff = Math.abs(bNow.getTime() - a.getTime());
    const totalDays = Math.floor(msDiff / 86400000);
    const totalHours = Math.floor(msDiff / 3600000);
    const totalMinutes = Math.floor(msDiff / 60000);
    const totalSeconds = Math.floor(msDiff / 1000);
    const totalWeeks = Math.floor(totalDays / 7);

    // Day of week born
    const weekdayName = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][a.getDay()];

    // Next birthday countdown
    const now = new Date();
    const nextBd = new Date(now.getFullYear(), a.getMonth(), a.getDate());
    if (nextBd.getTime() < now.getTime()) nextBd.setFullYear(now.getFullYear() + 1);
    const bdMs = nextBd.getTime() - now.getTime();
    const bdDays = Math.floor(bdMs / 86400000);
    const bdHours = Math.floor((bdMs % 86400000) / 3600000);
    const bdMinutes = Math.floor((bdMs % 3600000) / 60000);
    const bdSeconds = Math.floor((bdMs % 60000) / 1000);

    // Life-stats
    const heartbeats = Math.floor(totalMinutes * 72);
    const breaths = Math.floor(totalMinutes * 16);
    const sleepYears = ((totalDays * 8) / 24 / 365.25).toFixed(1);

    // Next landmark
    const landmarks = [16, 18, 21, 25, 30, 40, 50, 60, 70, 80, 90, 100];
    const nextLandmark = landmarks.find((l) => l > years) ?? years + 10;
    const nlDate = new Date(a); nlDate.setFullYear(a.getFullYear() + nextLandmark);
    const nlDays = Math.max(0, Math.ceil((nlDate.getTime() - now.getTime()) / 86400000));

    return {
      years, months, days, totalDays, totalHours, totalMinutes, totalSeconds, totalWeeks, forward,
      weekdayName, bdDays, bdHours, bdMinutes, bdSeconds,
      heartbeats, breaths, sleepYears, nextLandmark, nlDays,
    };
  }, [dob, target, today]);

  const shareMessage = result
    ? `🎉 My Accurate Age Profile!\n\n📅 Age: ${result.years} Years, ${result.months} Months, ${result.days} Days\n⏱️ Exact time lived: ${result.totalHours.toLocaleString()} Hours, ${result.totalMinutes.toLocaleString()} Minutes!\n🎂 Next Birthday Countdown: ${result.bdDays} Days left!\n\nCheck your precise life statistics instantly on https://drhint.com/age-calculator`
    : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">Tools / Age Calculator</nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{H1}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{DESC}</p>
      </section>

      <section className="mx-auto max-w-5xl px-4">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Date of Birth" value={dob} onChange={onEditDob} max={today} />
            <TripleDateInput label="Target Date" value={target} onChange={onEditTarget} quickChips />
          </div>

          <AnalyzeButton onClick={onCalculate} disabled={!canCalculate || analyzing} />

          <AnalyzingOverlay visible={analyzing} />

          <ReservedResultsArea minHeight={260}>
            {revealed && result ? (
              <>
                <section aria-label="Chronological Age Result">
                  <h2 className="sr-only">Exact Chronological Age in Years, Months and Days from Date of Birth</h2>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <ResultCard label="Age" value={`${result.years}y ${result.months}m ${result.days}d`} sub={result.forward ? "Completed" : "Before DOB"} />
                  </div>
                </section>

                <section aria-label="Granular Time Matrix" className="mt-3">
                  <h2 className="sr-only">Calculate Age in Total Weeks, Days, Hours, Minutes and Seconds</h2>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <ResultCard label="Total Weeks" value={result.totalWeeks.toLocaleString()} />
                    <ResultCard label="Total Days" value={result.totalDays.toLocaleString()} />
                    <ResultCard label="Total Hours" value={result.totalHours.toLocaleString()} sub="Hours lived" />
                    <ResultCard label="Total Minutes" value={result.totalMinutes.toLocaleString()} sub="Minutes lived" />
                    <ResultCard label="Total Seconds" value={result.totalSeconds.toLocaleString()} sub="Live ticking" />
                  </div>
                </section>

                <div className="mt-6 rounded-2xl border border-border bg-gradient-to-br from-primary/5 via-card to-card p-5">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Day of birth</div>
                  <div className="mt-1 text-xl md:text-2xl font-bold">You were born on a beautiful <span className="text-primary">{result.weekdayName}</span> ✨</div>
                </div>

                <section aria-label="Birthday Countdowns and Life Milestones">
                <h2 className="sr-only">Days Until Turning 30, 40, 50 Landmark Countdowns</h2>
                <div className="mt-6 grid gap-3 sm:grid-cols-4">
                  {[
                    { l: "Days", v: result.bdDays },
                    { l: "Hours", v: result.bdHours },
                    { l: "Minutes", v: result.bdMinutes },
                    { l: "Seconds", v: result.bdSeconds },
                  ].map((x) => (
                    <div key={x.l} className="rounded-2xl border border-border bg-card p-4 text-center" style={{ minHeight: 84 }}>
                      <div className="text-3xl font-black tabular-nums text-primary">{String(x.v).padStart(2, "0")}</div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{x.l} to Birthday</div>
                    </div>
                  ))}
                </div>
                </section>

                <section aria-label="Lifetime Biological Metrics">
                <h2 className="sr-only">Lifetime Heartbeats, Breaths Taken and Sleep Duration Calculator</h2>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Heartbeats" value={result.heartbeats.toLocaleString()} sub="≈ 72 BPM average" />
                  <ResultCard label="Breaths Taken" value={result.breaths.toLocaleString()} sub="≈ 16 breaths / min" />
                  <ResultCard label="Time Asleep" value={`${result.sleepYears} yrs`} sub="≈ 8 hours per day" />
                  <ResultCard label={`Turning ${result.nextLandmark}`} value={`${result.nlDays.toLocaleString()} d`} sub="Landmark countdown" />
                </div>
                </section>

                <section aria-label="Astrology, Numerology and Planetary Ages">
                  <h2 className="sr-only">Age on Other Planets (Mars, Venus, Jupiter) and Life Path Number</h2>
                  <AgeInsightCards dob={dob} years={result.years} totalDays={result.totalDays} weekdayName={result.weekdayName} />
                </section>

                <WhatsAppShareButton message={shareMessage} label="Share Age on WhatsApp" />

              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">
                Fill both dates and tap <span className="font-semibold text-primary">Calculate Exact Age</span> to reveal your live timeline.
              </div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <ContentSection title="How to Calculate Age from Date of Birth Online">
        <p>
          Calculating age from a date of birth (DOB) sounds simple — subtract one year from another — but the calendar
          gets in the way. Months have different lengths, leap years add a 29th of February every four years, and time
          zones can shift a birth date by a full day. A proper <strong>age calculator by DOB</strong> handles all three
          quietly so you never have to.
        </p>
        <h3>How Old Am I Today? — the exact formula</h3>
        <p>
          The standard rule is: subtract birth year from current year, then adjust down by one if the current month/day
          hasn't reached the birth month/day yet. Our calculator applies this rule and then computes the residual months
          and days by borrowing from the previous calendar month, so you always get a whole-number, government-form-ready
          answer.
        </p>
        <h3>Age between two custom dates</h3>
        <p>
          Set the target date to anything — past, present or future. This is useful for exam eligibility ("age as on 1 Jan
          2026"), passport applications, retirement planning, or simply counting the days until your next milestone
          birthday. For structured differentials in weeks and intervening months, jump to our{" "}
          <Link to="/age-calculator/chronological-date-difference" className="text-primary underline">Chronological & Date Difference Calculator</Link>.
        </p>
        <h3>Beyond chronological age</h3>
        <p>
          Chronological age is only one lens. Your <Link to="/age-calculator/biological-health-bmi" className="text-primary underline">biological age</Link>,
          your pet's <Link to="/age-calculator/pet-dog-cat" className="text-primary underline">equivalent human age</Link>,
          or a mother's <Link to="/age-calculator/gestational-pregnancy" className="text-primary underline">gestational week count</Link> all
          use different formulas — and DrHint gives each one a dedicated, purpose-built tool below.
        </p>
      </ContentSection>

      <article className="mx-auto mt-10 max-w-3xl px-4 text-sm leading-relaxed text-muted-foreground">
        <h2 className="mb-3 text-2xl font-bold text-foreground">How Your Exact Age and Lifetime Statistics Are Calculated</h2>
        <p className="mb-4">Our online age calculator processes your Date of Birth (DOB) against your target date or current live system clock. Taking leap years into account, the algorithm normalizes calendar gaps into precise completed years, months, total weeks, days, hours, and a live ticking second counter.</p>
        <h3 className="mt-6 mb-2 text-lg font-semibold text-foreground">Calculating Lifetime Heartbeats, Breaths, and Sleep Duration</h3>
        <p className="mb-4">To estimate how many heartbeats you have experienced since birth, the tool multiplies your total lived minutes by an average resting human heart rate of 72 beats per minute (BPM). Lifetime breaths taken are calculated using a baseline average of 16 breaths per minute. Time spent asleep is derived assuming an average of 8 hours of daily sleep (33.3% of your total lifespan).</p>
        <h3 className="mt-6 mb-2 text-lg font-semibold text-foreground">Planetary Age Calculation: Mars, Venus, and Jupiter</h3>
        <p className="mb-3">Your age on other planets depends on each planet's orbital revolution period around the Sun compared to Earth's 365.25 days:</p>
        <ul className="mb-4 list-disc pl-5">
          <li><strong>Mars Age:</strong> 1 Mars orbital year = 687 Earth days (Earth Age ÷ 1.88).</li>
          <li><strong>Jupiter Age:</strong> 1 Jupiter orbital year = 4,333 Earth days (Earth Age ÷ 11.86).</li>
          <li><strong>Venus Age:</strong> 1 Venus orbital year = 224.7 Earth days (Earth Age ÷ 0.615).</li>
        </ul>
        <h3 className="mt-6 mb-2 text-lg font-semibold text-foreground">Astrology Profile and Life Path Numerology Derivation</h3>
        <p>Your Life Path Number is calculated by reducing all digits of your full birth date (DD/MM/YYYY) down to a single master digit. Your zodiac sign, birthstone, and lucky traits are automatically mapped using astrological calendar date ranges.</p>
      </article>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />

      {/* BMI Calculator Directory — single-link entrypoint into the 200-page pSEO cluster */}
      <section className="mx-auto max-w-5xl px-4 mt-10">
        <Link
          to="/bmi-calculator"
          className="block rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6 hover:border-primary/60 transition"
        >
          <div className="text-xs uppercase tracking-wider text-primary font-semibold">Explore the full matrix</div>
          <div className="mt-2 text-xl md:text-2xl font-black tracking-tight">📊 BMI Calculator Directory — 200 pages</div>
          <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
            Age-specific, women-specific, exact-height and South Asian (WHO Asian cut-off) BMI calculators — one page per
            demographic slice, each with its own preventive-action framework.
          </p>
        </Link>
      </section>

      {/* 50-page global utility tool cluster — direct access grid */}
      <section className="mx-auto max-w-5xl px-4 mt-6">
        <div className="rounded-3xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-wider text-primary font-semibold">Free instant calculators</div>
          <div className="mt-2 text-xl md:text-2xl font-black tracking-tight">🧰 50 Utility Tools — direct access</div>
          <p className="mt-2 text-sm text-muted-foreground max-w-3xl">
            Fitness, money, date &amp; time, creator and home tools — each one runs instantly in your browser.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ALL_TOOLS.map((t) => (
              <Link
                key={t.slug}
                to="/tools/$tool"
                params={{ tool: t.slug }}
                className="rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold transition hover:border-primary/60"
              >
                {CLUSTER_EMOJI[t.cluster]} {t.name}
              </Link>
            ))}
          </div>
          <Link to="/tools/all" className="mt-4 inline-block text-sm text-primary underline">
            Browse the full tools directory →
          </Link>
        </div>
      </section>
    </Layout>
  );
}